import { Directive, OnDestroy, Input, Output, EventEmitter, HostListener, NgZone} from '@angular/core';
import { Subscription, takeUntil, timer } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';

@Directive({
  selector: '[longPress]',
  standalone: false
})
export class LongPressDirective implements OnDestroy {
  @Input() longPressDuration = 500;
  @Input() longPressMoveThreshold = 9;
  @Input() longPressDebugLabel = 'unknown'; 
  
  @Output() longPressStart = new EventEmitter<PointerEvent>();
  @Output() longPress = new EventEmitter<PointerEvent>();      // Main event
  @Output() longPressCancel = new EventEmitter<PointerEvent>();
  
  private destroy$ = new Subject<void>();
  private longPressCancel$ = new Subject<void>();
  private startX = 0;
  private startY = 0;
  private timerSubscription?: Subscription;

   constructor(private zone: NgZone) {}

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
  //  console.log(`[${this.longPressDebugLabel}] pointerdown`, event.target);
    event.stopPropagation();
    this.startX = event.clientX;
    this.startY = event.clientY;
    
    this.longPressStart.emit(event);  // Immediate feedback

    // console.log(`[${this.longPressDebugLabel}] Starting ${this.longPressDuration}ms timer`);
    
    this.timerSubscription = timer(this.longPressDuration)
      .pipe(
        takeUntil(this.longPressCancel$),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
       //  console.log(`[${this.longPressDebugLabel}] Timer completed - emitting longPress event`);
       this.zone.run(() => {
          this.longPress.emit(event);  // Success after duration
        });
      });
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const deltaX = Math.abs(event.clientX - this.startX);
    const deltaY = Math.abs(event.clientY - this.startY);
    
    if (deltaX > this.longPressMoveThreshold || deltaY > this.longPressMoveThreshold) {
    //  console.log(`[${this.longPressDebugLabel}] Movement detected (${deltaX}, ${deltaY}) - canceling`);
      this.cancel(event);
    }
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
   //  console.log(`[${this.longPressDebugLabel}] pointerup/cancel`);
    this.cancel(event);
  }

  private cancel(event: PointerEvent): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
    this.longPressCancel$.next();
    this.longPressCancel.emit(event);
  }

  ngOnDestroy(): void {
     //console.log(`[${this.longPressDebugLabel}] Directive destroyed`);
    this.destroy$.next();
    this.destroy$.complete();
    this.longPressCancel$.complete();
    if (this.timerSubscription) {
      console.log(`[${this.longPressDebugLabel}] Cleaning up timer subscription on destroy`);
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
  }
}